import React, { useState, useEffect } from 'react'
import { useLocation, globalHistory, useNavigate } from '@reach/router'
import { useMutation } from 'react-query'
import styled from 'styled-components'
import { navigate } from 'gatsby'
import Button from '../../components/Button'
import Heading from '../../components/Heading'
import Layout from '../../components/Layout'
import Table from '../../components/Table'
import { PlaceHolder } from '../../utils/withLoading'
import { useWithAuth, useLogout } from '../../containers/App'
import { useNotifications } from '../../containers/Notifications'
import UnitForm from './Form'
import AlertModal from './Modal'
import type { Units, UnitsListProps, RSPO, ModalStruct } from './utils'
// @ts-ignore @todo add apprioprate declaration file
import closeIconRed from '../../images/closeIcon-red.svg'

//const endpoint = '/report/rest/examination'

function NewUnit({ ...props }) {
  const withAuth = useWithAuth()
  const logout = useLogout()
  const endpoint = '/diagnostic/rest/unit'
  const endpointSearch = '/diagnostic/rest/rspo'
  const [importedUnit, setImportedUnit] = useState<UnitsListProps>({
    address: '',
    city: '',
    creationDate: null,
    id: null,
    name: '',
    postcode: '',
    publicUnit: null,
    rspo: -1,
    voivodeship: '',
  })
  const [alertModal, setAlertModal] = useState<ModalStruct>({
    isOpen: null,
    isLogout: false,
    redirect: '',
  })
  const [ready, setReady] = useState<boolean>(true)
  const { notify } = useNotifications()
  const [mutate] = useMutation<any, RSPO>((values) =>
    withAuth(endpoint, {
      method: 'POST',
      payload: values,
    })
      .then((res) => {
        if ('fieldErrors' in res) {
          // @todo notifications
        } else {
          setReady(true)
          notify('Dodano jednostkę', 'success')
          navigate(`/app/units/${res.id}`, {
            state: {
              unit: { ...values, id: res.id },
            },
          })
        }
      })
      .catch((e) => {
        e.status === 409 &&
          notify('Taki identyfikator RSPO już istnieje', 'error')
        setReady(true)
      })
  )
  const searchUnits = async (value) => {
    setReady(false)
    const result = await withAuth(`${endpointSearch}/${value}`, {
      method: 'GET',
    })
    return result
  }

  const handleSubmit = async (values: Units) => {
    try {
      const result = await searchUnits(values.rspo)
      if (result?.status !== 400) {
        // for some reason you cannot catch this error, comes back as results body
        if (result.id !== null) {
          notify('Taki identyfikator RSPO już istnieje', 'error')
        } else {
          setImportedUnit(result)
          setReady(true)
          setAlertModal({ ...alertModal, isOpen: false })
        }
      } else {
        notify('RSPO nie jest liczbą całkowitą nieujemną', 'error')
      }
    } catch (e) {
      switch (e.status) {
        case 404: {
          notify('Nie odnaleziono takiego id w RSPO', 'error')
          break
        }
        case 421: {
          notify('Brak poprawnej odpowiedzi z RSPO', 'error')
          break
        }
        default: {
          notify('Nie udało się pobrać danych jednostki', 'error')
        }
      }
    }
    setReady(true)
  }

  const handleSave = () => {
    setReady(false)
    const normalizedValues = {
      rspo: importedUnit.rspo,
    }
    mutate(normalizedValues)
    setImportedUnit({ ...importedUnit, rspo: -1 })
    setAlertModal({
      isOpen: null,
      isLogout: false,
      redirect: '',
    })
  }
  const curLocation = useLocation()
  const reachNavigate = useNavigate()

  const handleReturn = () => {
    if (alertModal.isLogout) {
      logout()
    } else {
      reachNavigate(`${alertModal.redirect}`)
    }
  }

  useEffect(() => {
    return globalHistory.listen(({ action, location }) => {
      if (
        alertModal.isOpen === false &&
        (action === 'POP' || action === 'PUSH')
      ) {
        setAlertModal({
          isOpen: true,
          isLogout: false,
          redirect: location.pathname,
        })
        reachNavigate(`${curLocation.pathname}`)
      }
    })
  }, [alertModal])

  return (
    <Layout
      modalLogout={
        alertModal.isOpen === false
          ? () =>
              setAlertModal({
                isOpen: true,
                isLogout: true,
                redirect: '',
              })
          : null
      }
    >
      <Heading title="Nowa Jednostka">
        <Button
          link="/app/units"
          label="Powrót"
          icon={closeIconRed}
          width="auto"
          color="white"
          altTag=""
          imgAriaHidden={true}
          srText=""
        />
      </Heading>
      <PanelWrapper>
        <Panel>
          <h1>Informacje o jednostce</h1>
          <LoaderWrapper ready={ready}>
            <PlaceHolder />
          </LoaderWrapper>
          <FormWrapper style={{ visibility: ready ? 'visible' : 'hidden' }}>
            <UnitForm
              onSubmit={handleSubmit}
              newModel
              importedUnit={importedUnit}
              handleSave={handleSave}
            />
          </FormWrapper>
        </Panel>
        <Panel>
          <h2>Połączone osoby</h2>
          <Table
            defaultPageSize={0}
            getLoadingProps={() => ({
              'aria-hidden': true,
              role: 'none',
            })}
            getTheadTrProps={() => ({
              style: { display: 'flex', alignItems: 'stretch' },
            })}
            columns={[
              {
                Header: 'Imię i nazwisko',
                id: 'fullname',
                sortable: false,
                accessor: ({ firstName, lastName }) =>
                  `${firstName} ${lastName}`,
                getProps: () => ({
                  role: undefined,
                }),
              },

              {
                Header: 'Rola',
                sortable: false,
                id: 'roles',
                accessor: ({ roles }) => roles.map((role) => `${role.name}`),
                getProps: () => ({
                  role: undefined,
                }),
              },

              {
                Header: 'Akcje',
                sortable: false,
              },
            ]}
          />
        </Panel>
      </PanelWrapper>
      <AlertModal
        isOpen={alertModal.isOpen}
        isLogout={alertModal.isLogout}
        onReturn={() => handleReturn()}
        onClose={() =>
          setAlertModal({ isOpen: false, isLogout: false, redirect: '' })
        }
      />
    </Layout>
  )
}

export default NewUnit

const PanelWrapper = styled.div`
  h2,
  h1 {
    font-size: 1.5em;
    font-weight: 400;
  }
  margin: 2em;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr;
  gap: 0px 2em;
  grid-template-areas: '. .';
  justify-content: space-between;
`
const Panel = styled.div`
  padding: 2em;
  border-radius: 6px;
  border: 1px solid #ddd;
`

const FormWrapper = styled.div`
  margin-top: 5rem;
`
const LoaderWrapper = styled.div`
  position: relative;
  top: 35%;
  display: ${(props) => (props.ready ? `none` : 'block')};
`